import { useCallback, useEffect, useRef, useState } from "react";
import { activityLog } from "@/features/activityLog";
import { currentReturnPath } from "@/features/tokenPurchase/model/returnPath";
import { pushThenNavigate } from "@/shared/lib/analytics";
import { FoodPackError, startFoodPackCheckout } from "../api/foodPack";
import type { FoodPackOffer } from "../types";

/**
 * Что сказать, когда к оплате не перешли: «auth» — войти заново (сеанс истёк или это не свой вход), «retry» —
 * попробовать ещё раз (сеть, сбой, много запросов), «unavailable» — временно недоступно (503 сервера: запрет записи
 * диагностики не подтверждён — это не «нет диагностики»). Диагностики диеты больше нет — не ошибка листа:
 * onNoDiagnostic (лист спросит счётчик и покажет блок «после диагностики»). 503 diagnostic_unavailable, когда есть
 * onDiagUnavailable, — тоже не ошибка листа лимита: счётчику, по которому он предлагает пакет, уже не верим (лист
 * распознавания закроет его и спросит счётчик снова, как при 503 на вопрос о счётчике).
 */
export type FoodPackCheckoutError = "auth" | "retry" | "unavailable";

const AUTH_CODES = new Set(["auth_required", "session_not_allowed"]);
const UNAVAILABLE_CODES = new Set(["diagnostic_unavailable", "quota_unavailable"]);

/**
 * Продлевали идущий пакет — его конец до оплаты (offer.starts_at: купленное начнётся с него). После возврата со Stripe
 * FoodPackPurchaseRoot сверяет с ним pack.until: конец не сдвинулся — продление ещё не выдано, «добавятся в течение
 * нескольких минут», а не «Готово … до» прежней даты. Пакета не было — ничего не храним.
 */
export const PENDING_FOOD_PACK_KEY = "pending_food_pack";

export const rememberPackEnd = (startsAt: string | null | undefined): void => {
    try {
        if (startsAt) sessionStorage.setItem(PENDING_FOOD_PACK_KEY, JSON.stringify({ until: startsAt, at: Date.now() }));
        else sessionStorage.removeItem(PENDING_FOOD_PACK_KEY);
    } catch {
        /* без sessionStorage — строка по счётчику, как раньше */
    }
};

/** Конец пакета до оплаты (мс) или null — пакета не было или не знаем. Читается один раз. */
export const takePackEnd = (): number | null => {
    try {
        const raw = sessionStorage.getItem(PENDING_FOOD_PACK_KEY);
        sessionStorage.removeItem(PENDING_FOOD_PACK_KEY);
        if (!raw) return null;
        const ms = new Date(String((JSON.parse(raw) as { until?: unknown }).until)).getTime();
        return isNaN(ms) ? null : ms;
    } catch {
        return null;
    }
};

/**
 * Оплата пакета «+60 распознаваний», как у пакетов поиска (supPlan/model/usePlanCheckout): сервер выбирает цену и
 * отдаёт ссылку Stripe, переход — после события журнала (pushThenNavigate). Вернулись кнопкой «назад» браузера со
 * Stripe (страница из bfcache) — «идёт оплата» снимается.
 */
export const useFoodPackCheckout = (
    lang: string,
    place = "food_limit",
    onNoDiagnostic?: () => void,
    onDiagUnavailable?: () => void,
) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<FoodPackCheckoutError | null>(null);
    const noDiagRef = useRef(onNoDiagnostic);
    noDiagRef.current = onNoDiagnostic;
    const diagUnavailableRef = useRef(onDiagUnavailable);
    diagUnavailableRef.current = onDiagUnavailable;

    useEffect(() => {
        const onShow = (e: PageTransitionEvent) => {
            if (e.persisted) setBusy(false);
        };
        window.addEventListener("pageshow", onShow);
        return () => window.removeEventListener("pageshow", onShow);
    }, []);

    const buy = useCallback(
        async (offer: FoodPackOffer) => {
            if (busy) return;
            setBusy(true);
            setError(null);
            try {
                const { url } = await startFoodPackCheckout({ returnPath: currentReturnPath(), lang });
                rememberPackEnd(offer.startsAt);
                await pushThenNavigate(
                    () =>
                        activityLog.track("checkout_redirect", {
                            kind: "food_pack",
                            sku: offer.product || "food-pack",
                            place,
                        }),
                    url,
                );
            } catch (err) {
                const code = err instanceof FoodPackError ? err.code : "";
                activityLog.track("checkout_error", { kind: "food_pack", place, ...(code ? { code } : {}) });
                setBusy(false);
                if (code === "no_diet_diagnostic") {
                    noDiagRef.current?.();
                    return;
                }
                if (code === "diagnostic_unavailable" && diagUnavailableRef.current) {
                    diagUnavailableRef.current();
                    return;
                }
                setError(AUTH_CODES.has(code) ? "auth" : UNAVAILABLE_CODES.has(code) ? "unavailable" : "retry");
            }
        },
        [busy, lang, place],
    );

    return { buy, busy, error };
};
